/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Line } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// GradientLineChart configurations
import configs from "examples/Charts/LineCharts/GradientLineChart/configs";

// Soft UI Dashboard React base styles
import colors from "assets/theme/base/colors";

function GradientLineChart({ title, description, height, chart, showLegend }) {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState({});
  const { data, options } = chartData;

  useEffect(() => {
    const chartDatasets = chart?.datasets
      ?         chart.datasets.map((dataset) => {
          const { gradientFillOpacity: gfOp, ...restDataset } = dataset;
          const lineColor = colors[dataset.color]
            ? colors[dataset.color || "dark"].main
            : colors.dark.main;
          const useFill = dataset.fill !== false;
          const fillOpacity =
            gfOp !== undefined ? gfOp : 0.22;
          return {
            ...restDataset,
            tension: 0.4,
            pointRadius: 0,
            borderWidth: 3,
            borderColor: lineColor,
            fill: useFill,
            maxBarThickness: 6,
            backgroundColor: useFill
              ? gradientChartLine(chartRef.current?.children[0], lineColor, fillOpacity)
              : "transparent",
          };
        })
      : [];

    const base = configs(chart?.labels || [], chartDatasets);
    setChartData({
      ...base,
      options: {
        ...base.options,
        plugins: {
          ...base.options.plugins,
          legend: {
            ...(base.options.plugins?.legend || {}),
            display: Boolean(showLegend),
            position: "bottom",
          },
        },
      },
    });
  }, [chart, showLegend]);

  const renderChart = (
    <SoftBox p={2}>
      {title || description ? (
        <SoftBox px={description ? 1 : 0} pt={description ? 1 : 0}>
          {title && (
            <SoftBox mb={1}>
              <SoftTypography variant="h6">{title}</SoftTypography>
            </SoftBox>
          )}
          <SoftBox mb={2}>
            <SoftTypography component="div" variant="button" fontWeight="regular" color="text">
              {description}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
      ) : null}
      {useMemo(
        () => (
          <SoftBox ref={chartRef} sx={{ height }}>
            <Line data={data} options={options} />
          </SoftBox>
        ),
        [chartData, height]
      )}
    </SoftBox>
  );

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Setting default values for the props of GradientLineChart
GradientLineChart.defaultProps = {
  title: "",
  description: "",
  height: "19.125rem",
  showLegend: false,
};

// Typechecking props for the GradientLineChart
GradientLineChart.propTypes = {
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  chart: PropTypes.objectOf(PropTypes.array).isRequired,
  showLegend: PropTypes.bool,
};

export default GradientLineChart;
